import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Coffee, Ear, Lock } from "lucide-react";
import client from "../../lib/api";
import iconMark from "../../assets/brand/klareet-icon-white.png";

/**
 * A5: Meet Klair. A single-screen hello after consent, before the
 * intake. It reads like a friend saying hi, greets the person by
 * name when we have it, and carries them into the education step.
 */
export default function MeetKlairScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [ready, setReady] = useState(false);
  const [name, setName] = useState("");
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 120);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let alive = true;
    client
      .get("/consent/status")
      .then((r) => {
        if (!alive) return;
        const full = (r.data?.display_name || "").trim();
        if (full) setName(full.split(/\s+/)[0]);
      })
      .catch(() => {})
      .finally(() => { if (alive) setResolved(true); });
    return () => { alive = false; };
  }, []);

  const greeting = name ? `Hey ${name}.` : "Hey there.";

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-blue"  style={{ top: "-8rem", left: "-6rem",  width: "24rem", height: "24rem" }} />
      <div className="k-orb k-orb-teal"  style={{ top: "8rem",  right: "-8rem", width: "22rem", height: "22rem" }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-6rem", left: "20%", width: "18rem", height: "18rem" }} />

      <div className="relative max-w-4xl mx-auto px-5 pt-16 pb-24 text-center">
        <div
          className="mx-auto mb-8 relative w-28 h-28 rounded-[28px] flex items-center justify-center k-fade-in"
          style={{ background: "linear-gradient(135deg, #1A1D3A, #2B2150 60%, #3C1B4E)" }}
        >
          <span className="k-ring-pulse" aria-hidden="true"></span>
          <img src={iconMark} alt="" aria-hidden="true" className="w-16 h-16" />
        </div>

        <div className="k-fade-in" style={{ animationDelay: "80ms" }}>
          <span className="k-chip k-chip-teal mb-4 mx-auto"><Sparkles size={12} /> say hi to Klair</span>
          <h1
            className="k-hero-title mb-4"
            data-testid="meet-title"
            style={{
              opacity: resolved ? 1 : 0,
              transition: "opacity 320ms ease",
              fontSize: "clamp(1.9rem, 4.4vw, 3.1rem)",
              lineHeight: 1.1,
            }}
          >
            {greeting} I am <span style={{ color: "hsl(var(--klareet-blue))" }}>Klair</span>.
          </h1>
          <p className="k-hero-sub mx-auto mb-10" style={{ maxWidth: "38rem", fontSize: "1.02rem" }}>
            Think of this like grabbing coffee with a friend who is really good
            at reading people. I will ask you about seventeen things, one at a
            time, and I actually remember what you tell me. At the end you get a
            read on what genuinely fits you, and what sits underneath it. No test,
            no scores, no judgement.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-12 max-w-4xl mx-auto k-fade-in" style={{ animationDelay: "180ms" }}>
          <Bullet
            icon={<Coffee size={22} />}
            title="We just talk"
            body="One thing at a time, like texting a friend. No forms, no timers, no pressure."
          />
          <Bullet
            icon={<Ear size={22} />}
            title="I actually listen"
            body="Every answer you give shapes what I ask next. This is your conversation, not a script."
          />
          <Bullet
            icon={<Lock size={22} />}
            title="This stays yours"
            body="Your words are yours. Colleges and employers only ever see anonymous group patterns, never you."
          />
        </div>

        <button
          onClick={() => nav(`${base}/onboarding/education`)}
          disabled={!ready}
          className="k-btn k-btn-accent k-btn-lg"
          data-testid="meet-continue"
        >
          Let us start, three quick things first <ArrowRight size={18} />
        </button>
        <p className="k-quiet mt-4">Takes about ten minutes. You can stop any time.</p>
      </div>
    </div>
  );
}

function Bullet({ icon, title, body }) {
  return (
    <div className="k-meet-card k-fade-in">
      <div className="k-meet-card-icon">{icon}</div>
      <div className="k-meet-card-title">{title}</div>
      <div className="k-meet-card-body">{body}</div>
    </div>
  );
}
